/**
 * @fileoverview React hooks for resolving uploaded-file IDs to displayable URLs.
 * @module file-service-client/hooks
 *
 * Responsibilities: - useUploadedFileUrl: resolve a single file ID to a SAS URL.
 *                   - useResolvedLogoUrls: batch-resolve all uploaded-logo IDs in a
 *                     DocumentLayout to SAS URLs (for thumbnails + preview).
 * Dependencies:     - react, ../client/urlResolver.
 * Architecture:     - Hooks call the singleton FileUrlResolver (initialized by app bootstrap).
 *                   - Cleanup is delegated to the resolver's LRU cache.
 * Notes:            - These hooks are the ONLY way page components should resolve
 *                     uploaded-file URLs. Never call the API directly from a component.
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import { getFileUrlResolver, tryGetFileUrlResolver } from '../client/urlResolver';

/**
 * @hook useUploadedFileUrl
 * Purpose:        Resolve a single uploaded-file ID to a displayable SAS URL.
 * Returns:        { url, loading, error }
 * Side Effects:   Calls the FileUrlResolver singleton (HTTP if cache miss).
 * Offline Behavior: Returns null when the resolver can't reach either API;
 *                   the cache may still serve a previously-resolved URL.
 */
export function useUploadedFileUrl(
  fileId: string | null | undefined,
): { url: string | null; loading: boolean; error: Error | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(fileId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!fileId) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resolver = getFileUrlResolver();
        const resolved = await resolver.resolve(fileId);
        if (cancelled) return;
        setUrl(resolved);
      } catch (err) {
        if (cancelled) return;
        setError(err as Error);
        setUrl(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fileId]);

  return { url, loading, error };
}

/**
 * @hook useResolvedLogoUrls
 * Purpose:        Batch-resolve all uploaded-file IDs in a layout to SAS URLs.
 * Returns:        Record<fileId, sasUrl> — use this to feed resolveLogoThumb
 *                  and resolveDocumentPreviewData's resolveUploadedFileUrl argument.
 * Side Effects:   Calls the FileUrlResolver singleton for each uncached ID.
 * Offline Behavior: Returns partial results — IDs that can't be resolved are
 *                   simply absent from the record; the caller handles undefined.
 *
 * Usage:
 *   const logoUrls = useResolvedLogoUrls(documentLayout, collectUploadedLogoIds);
 *   const resolveLogoThumb = (fid) => logoUrls[fid];
 */
export function useResolvedLogoUrls<T>(
  layout: T | null | undefined,
  collectIds: (layout: T) => string[],
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});

  // Collect IDs from the layout, de-duplicated.
  const ids = useMemo(() => {
    if (!layout) return [];
    return collectIds(layout as T);
  }, [layout, collectIds]);

  // Track which IDs we've already attempted so we don't re-fetch on every render.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());

  // Track previous layout reference to detect switches (e.g. selecting a different
  // template). Using a ref + single effect instead of two separate effects prevents
  // a wasted resolution attempt: the old two-effect pattern fired the resolve effect
  // before the reset effect on layout change, starting (then cancelling) a redundant
  // HTTP round-trip.
  const prevLayoutRef = useRef(layout);

  useEffect(() => {
    // Layout changed — reset tracking and return. The state update triggers a
    // re-render; on the next pass layout is unchanged so we fall through to resolve.
    if (prevLayoutRef.current !== layout) {
      prevLayoutRef.current = layout;
      setUrls({});
      setResolvedIds(new Set());
      return;
    }

    const unresolved = ids.filter((id) => !resolvedIds.has(id));
    if (unresolved.length === 0) return;

    let cancelled = false;
    (async () => {
      const resolver = tryGetFileUrlResolver();
      if (!resolver) return;

      const resolved = await resolver.resolveMany(unresolved);
      if (cancelled) return;

      setUrls((prev) => ({ ...prev, ...resolved }));
      setResolvedIds((prev) => {
        const next = new Set(prev);
        for (const id of unresolved) next.add(id);
        return next;
      });
    })();

    return () => { cancelled = true; };
  }, [ids, resolvedIds, layout]);

  return urls;
}
